import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Settings, Loader2, Percent, DollarSign } from "lucide-react";

export default function AdminGateway() {
  const { t } = useI18n();
  const [settings, setSettings] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [s, c] = await Promise.all([api.get("/admin/gateway-settings"), api.get("/currencies")]);
      setSettings(s.data); setCurrencies(c.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/gateway-settings", {
        percentage: parseFloat(settings.percentage), fixed: parseFloat(settings.fixed), fee_currency: settings.fee_currency,
      });
      toast.success(t("adm.gwSaved"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="w-48 ob-loader h-1 rounded-full bg-zinc-900 mx-auto mt-20" />;

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white flex items-center gap-3"><Settings className="text-[#00E5FF]" /> {t("adm.gwTitle")}</h1>
        <p className="text-zinc-500 text-sm mt-1">{t("adm.gwSub")}</p>
      </div>

      <div className="ob-card rounded-md p-7 space-y-5">
        <div>
          <label className="text-xs font-semibold tracking-widest text-zinc-400 uppercase flex items-center gap-1"><Percent size={13} /> {t("adm.gwPct")}</label>
          <input type="number" step="0.1" value={settings.percentage} onChange={(e) => setSettings({ ...settings, percentage: e.target.value })} dir="ltr" data-testid="gw-percentage"
            className="mt-2 w-full bg-[#121212] border border-zinc-800 rounded-md px-4 py-3 text-white text-end focus:border-[#00E5FF] outline-none" />
        </div>
        <div>
          <label className="text-xs font-semibold tracking-widest text-zinc-400 uppercase flex items-center gap-1"><DollarSign size={13} /> {t("adm.gwFixed")}</label>
          <input type="number" step="0.01" value={settings.fixed} onChange={(e) => setSettings({ ...settings, fixed: e.target.value })} dir="ltr" data-testid="gw-fixed"
            className="mt-2 w-full bg-[#121212] border border-zinc-800 rounded-md px-4 py-3 text-white text-end focus:border-[#00E5FF] outline-none" />
        </div>
        <div>
          <label className="text-xs font-semibold tracking-widest text-zinc-400 uppercase">{t("adm.gwFeeCur")}</label>
          <select value={settings.fee_currency} onChange={(e) => setSettings({ ...settings, fee_currency: e.target.value })} data-testid="gw-fee-currency"
            className="mt-2 w-full bg-[#121212] border border-zinc-800 rounded-md px-4 py-3 text-white focus:border-[#00E5FF] outline-none">
            {currencies.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
          </select>
        </div>
        <div className="bg-[#0A0A0A] border border-zinc-800 rounded-md p-4 text-sm text-zinc-400">
          <span dir="ltr">fee = amount × {settings.percentage}% + {settings.fixed}</span>
        </div>
        <button onClick={save} disabled={busy} data-testid="save-gateway-settings"
          className="w-full bg-[#00E5FF] text-black font-bold py-3 rounded-md hover:bg-[#33EBFF] flex items-center justify-center gap-2 disabled:opacity-60">
          {busy && <Loader2 size={18} className="animate-spin" />} {t("common.save")}
        </button>
      </div>
    </div>
  );
}
